import { api } from '@/lib/axios';

/**
 * AP-30-E — Rollen und Rechte des Mandanten (Recht `core.role.manage`).
 *
 * Liegt in `src/lib/`, nicht in einem Feature: Die Benutzerverwaltung braucht
 * die Rollenliste genauso wie die Rolleneinstellungen — dieselbe Einordnung wie
 * `lib/users/` und `lib/auth.store`. Rollen gehören zum Registry-Eintrag `CORE`
 * und damit zu keinem Fachmodul (AP-21-B: Geteiltes gehört nach `src/lib/`,
 * nicht über einen Querimport).
 */

export interface RoleView {
  id: string;
  key: string;
  name: string;
  description: string | null;
  /** Aus einer Systemvorlage — nicht bearbeitbar, aber kopierbar. */
  isSystem: boolean;
  templateKey: string | null;
  /** Die wirksamen Rechte; bei einer Systemrolle aus der Vorlage im Code. */
  permissions: string[];
  assignedCount: number;
}

export interface EffectiveRights {
  /** Was die Rollen dieses Benutzers **vergeben** haben. */
  granted: string[];
  /** Was davon **wirkt** — nach dem Schnitt gegen die Module mit Platz. */
  effective: string[];
  roles: { key: string; name: string }[];
}

export const rolesApi = {
  list: async (): Promise<RoleView[]> => (await api.get<RoleView[]>('/roles')).data,

  /** Vergebene und wirksame Rechte eines Benutzers — der Unterschied ist die Auskunft. */
  effectiveFor: async (userId: string): Promise<EffectiveRights> =>
    (await api.get<EffectiveRights>(`/roles/users/${userId}/effective`)).data,

  create: (dto: {
    key: string;
    name: string;
    description?: string;
    permissions: string[];
  }) => api.post<RoleView>('/roles', dto),

  /** Kopiert eine Systemvorlage in eine eigene Rolle — der Weg aus AP-30 §3. */
  copyTemplate: (dto: {
    templateKey: string;
    key: string;
    name: string;
    description?: string;
  }) => api.post<RoleView>('/roles/copy', dto),

  update: (
    id: string,
    dto: { name?: string; description?: string; permissions?: string[] },
  ) => api.patch<RoleView>(`/roles/${id}`, dto),

  remove: (id: string) => api.delete<{ success: true }>(`/roles/${id}`),

  assign: (roleId: string, userId: string) =>
    api.post<{ success: true }>(`/roles/${roleId}/users/${userId}`),

  unassign: (roleId: string, userId: string) =>
    api.delete<{ success: true }>(`/roles/${roleId}/users/${userId}`),
};
